import React, { useEffect, useRef, useState } from 'react';
import './MultiSelectCheckboxDropdown.css';

/**
 * Dropdown with a "Select All" checkbox plus one checkbox per option.
 * "Select All" and the individual boxes stay in sync in both directions:
 * checking it selects everything, unchecking it clears everything, and the
 * checked state reflects back to "checked" whenever every option ends up
 * selected by hand.
 */
function MultiSelectCheckboxDropdown({
  id,
  options = [],
  selected = [],
  onChange,
  placeholder = '-- Select --',
  allLabel = 'All',
  loading = false,
  disabled = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const allSelected = options.length > 0 && selected.length === options.length;

  const toggleSelectAll = () => {
    onChange(allSelected ? [] : [...options]);
  };

  const toggleOption = (option) => {
    onChange(
      selected.includes(option)
        ? selected.filter((item) => item !== option)
        : [...selected, option]
    );
  };

  const summaryLabel = () => {
    if (loading) return 'Loading locations...';
    if (!selected.length) return placeholder;
    if (allSelected) return allLabel;
    if (selected.length <= 2) return selected.join(', ');
    return `${selected.slice(0, 2).join(', ')} +${selected.length - 2} more`;
  };

  return (
    <div className="multiselect-dropdown" ref={containerRef}>
      <button
        type="button"
        id={id}
        className="multiselect-dropdown-trigger"
        onClick={() => setIsOpen((prev) => !prev)}
        disabled={disabled || loading}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className={selected.length ? '' : 'multiselect-dropdown-placeholder'}>
          {summaryLabel()}
        </span>
        <span className="multiselect-dropdown-caret" aria-hidden="true">&#9662;</span>
      </button>

      {isOpen && !disabled && !loading && (
        <div className="multiselect-dropdown-panel" role="listbox">
          <label className="multiselect-dropdown-item multiselect-dropdown-select-all">
            <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
            <span>{allLabel}</span>
          </label>
          <div className="multiselect-dropdown-divider" />
          {options.map((option) => (
            <label key={option} className="multiselect-dropdown-item">
              <input
                type="checkbox"
                checked={selected.includes(option)}
                onChange={() => toggleOption(option)}
              />
              <span>{option}</span>
            </label>
          ))}
          {!options.length && (
            <div className="multiselect-dropdown-empty">No options available.</div>
          )}
        </div>
      )}
    </div>
  );
}

export default MultiSelectCheckboxDropdown;
